import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { menuItems } from '../../../utils/menuItems';
import { useNavigate } from 'react-router-dom';
import UserSelection from './UserSelection/UserSelection';
import RestaurantSelector from '../RestaurantSelector/RestaurantSelector';
import './navigation.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../../context/AuthContext';
import useUserInfo from '../../../hooks/useUserInfo';
import logo from './logo.png';
import { userChefIcon, downArrowIcon, signOutIcon, profileIcon } from '../../../utils/icons';
import NotificationBell from './NotificationBell/NotificationBell';

export const Navigation = ({ active, setActive }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userType, setUserType] = useState('');
  const [profileImageUrl, setProfileImageUrl] = useState('');
  const [menuOpenProfessionals, setMenuOpenProfessionals] = useState(false);
  const [menuOpenCompanies, setMenuOpenCompanies] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const { isAuthenticated, userInfo, logout } = useAuth();
  const { userInfo: userInfoFromHook, loading: userInfoLoading, refreshUserInfo } = useUserInfo();

  const menuProfessionalsRef = useRef(null);
  const menuCompaniesRef = useRef(null);

  useEffect(() => {
    
    if (isAuthenticated && !userInfoLoading && userInfoFromHook) {
      setUserType(userInfoFromHook?.userType || '');
      setProfileImageUrl(userInfoFromHook?.profileImageUrl);
    } else if (!isAuthenticated) {
      setIsLoggedIn(false);
      setUserType('');
    }
  }, [isAuthenticated, userInfo, userInfoFromHook, userInfoLoading]);

  // Separate useEffect to handle userInfoFromHook changes
  useEffect(() => {
    if (isAuthenticated && userInfoFromHook) {
      setUserType(userInfoFromHook?.userType || '');
      setProfileImageUrl(userInfoFromHook?.profileImageUrl);
    }
  }, [userInfoFromHook, isAuthenticated]);

  // Listen for login events to refresh user info
  useEffect(() => {
    const handleUserLoggedIn = () => {
      refreshUserInfo();
    };

    window.addEventListener('userLoggedIn', handleUserLoggedIn);
    
    return () => {
      window.removeEventListener('userLoggedIn', handleUserLoggedIn);
    };
  }, [refreshUserInfo]);



  const handleLogout = async () => {
    logout()
    navigate('/')
  };

  const toggleProfileMenu = () => {
    setProfileMenuOpen(!profileMenuOpen);
  };

  const handleNavLinkClick = (item) => {
    setActive(item.title);
  };

  useEffect(() => {
    const handleClickOutsideProfile = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setProfileMenuOpen(false);
      }
    };

    const handleClickOutsideMenu = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    if (profileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutsideProfile);
    } else {
      document.removeEventListener('mousedown', handleClickOutsideProfile);
    }

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutsideMenu);
    } else {
      document.removeEventListener('mousedown', handleClickOutsideMenu);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutsideProfile);
      document.removeEventListener('mousedown', handleClickOutsideMenu);
    };
  }, [profileMenuOpen, menuOpen]);

  const handleProfessionalsClick = (e) => {
    e.stopPropagation();
    // Only toggle dropdowns in desktop view
    if (window.innerWidth > 1024) {
      setMenuOpenProfessionals(!menuOpenProfessionals);
      setMenuOpenCompanies(false);
    } else {
      setMenuOpenProfessionals(!menuOpenProfessionals);
      setMenuOpenCompanies(false);
      setMenuOpen(true);
    }
  };

  const handleRedirectToHome = () => {
    navigate('/');
  };

  const handleCompaniesClick = (e) => {
    e.stopPropagation();
    if (window.innerWidth > 1024) {
      setMenuOpenCompanies(!menuOpenCompanies);
      setMenuOpenProfessionals(false);
    } else {
      setMenuOpenCompanies(!menuOpenCompanies);
      setMenuOpenProfessionals(false);
      setMenuOpen(true);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (window.innerWidth > 1024) {
        if (menuProfessionalsRef.current &&
            !menuProfessionalsRef.current.contains(event.target)) {
          setMenuOpenProfessionals(false);
        }

        if (menuCompaniesRef.current &&
            !menuCompaniesRef.current.contains(event.target)) {
          setMenuOpenCompanies(false);
        }
      }
    };

    if (menuOpenProfessionals || menuOpenCompanies) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpenProfessionals, menuOpenCompanies]);

  return (
    <nav className="navigation__container">
      <div className="navigation__left-container">
        <Link to="/">
          <div className="navigation__logo-container">
            <img src={logo} alt="Logo" className="navigation__logo-image" />
          </div>
        </Link>

        <ul className={`menu-list ${menuOpen ? 'open' : ''}`} ref={menuRef}>
          {menuItems.map((item, index) => (
            <li key={item.id} className={`nav-icon-container ${index < 4 ? 'with-border' : ''}`}>
              <NavLink
                to={item.link}
                className={({ isActive }) => (isActive ? 'active' : '')}
                onClick={() => handleNavLinkClick(item)}
              >
                <span className="title-tab">{item.title}</span>
              </NavLink>
            </li>
          ))}

          {/* Mobile user selection using desktop structure */}
          {!isAuthenticated ? (
            <div className="navigation__user-selection-title-container mobile-only">
              <div className="user-selection-title" ref={menuProfessionalsRef}>
                <div className="navigation__logo-icon-container" onClick={handleProfessionalsClick}>
                  <h2>Trabajadores</h2>
                  {downArrowIcon}
                </div>
                {menuOpenProfessionals && <UserSelection userType="profesionales" />}
              </div>
              <div className="user-selection-title" ref={menuCompaniesRef}>
                <div className="navigation__logo-icon-container" onClick={handleCompaniesClick}>
                  <h2>Empresas</h2>
                  {downArrowIcon}
                </div>
                {menuOpenCompanies && <UserSelection userType="empresas" />}
              </div>
            </div>
          ) : (
            <div className="navigation__user-selection-title-container mobile-only">
              <NotificationBell userInfo={userInfoFromHook} />
              <div className="navigation__profile-menu-container">
                {profileImageUrl && profileImageUrl !== 'No photo' && profileImageUrl !== '' ? (
                  <img
                    className="navigation__profile-picture mobile-profile-spacing"
                    src={profileImageUrl}
                    alt="Profile"
                  />
                ) : (
                  <div className="navigation__profile-icon mobile-profile-spacing">
                    {userChefIcon}
                  </div>
                )}

                {/* Mobile Restaurant Selector */}
                {userInfoFromHook?.userType === "empresas" && (
                  <div className="navigation__mobile-restaurant-selector">
                    <RestaurantSelector />
                  </div>
                )}
                <div className="navigation__menu-item-container">
                  {userChefIcon}
                  <Link
                    to={userInfoFromHook?.userType === "empresas" ? "/panel-empresa/restaurantes" : "/panel-empleado/perfil-empleado"}
                    className="navigation__profile-menu-item"
                  >
                    Mi Perfil
                  </Link>
                </div>
                <div className="navigation__menu-item-container">
                  {signOutIcon}
                  <Link
                    onClick={handleLogout}
                    className="navigation__profile-menu-item"
                  >
                    Cerrar sesión
                  </Link>
                </div>
              </div>
            </div>
          )}
        </ul>
      </div>

      <div className="nav-right desktop-only">
        {!isAuthenticated ? (
          <div className="navigation__user-selection-title-container">
            <div className="user-selection-title" ref={menuProfessionalsRef}>
              <div className="navigation__logo-icon-container" onClick={handleProfessionalsClick}>
                <h2>Trabajadores</h2>
                {downArrowIcon}
              </div>
              {menuOpenProfessionals && <UserSelection userType="profesionales" />}
            </div>
            <div className="user-selection-title" ref={menuCompaniesRef}>
              <div className="navigation__logo-icon-container" onClick={handleCompaniesClick}>
                <h2>Empresas</h2>
                {downArrowIcon}
              </div>
              {menuOpenCompanies && <UserSelection userType="empresas" />}
            </div>
          </div>
        ) : (
          <div className="navigation__profile-logout-container">
            {userInfoFromHook?.userType === "empresas" && (
              <div className="navigation__restaurant-selector">
                <RestaurantSelector />
              </div>
            )}
            <NotificationBell userInfo={userInfoFromHook} />
            <div className="navigation__profile-menu-container" ref={profileMenuRef}>
              {profileImageUrl && profileImageUrl !== 'No photo' && profileImageUrl !== '' ? (
                <img
                  className="navigation__profile-picture mobile-profile-spacing"
                  onClick={toggleProfileMenu}
                  src={profileImageUrl}
                  alt="Profile"
                />
              ) : (
                <div
                  className="navigation__profile-icon mobile-profile-spacing"
                  onClick={toggleProfileMenu}
                >
                  {userChefIcon}
                </div>
              )}

              {profileMenuOpen && (
                <div className="navigation__profile-menu">
                  {userInfoFromHook?.userType === "empresas" ? (
                    <div>
                      <div
                        className="navigation__menu-item-container"
                        onClick={() => {
                          setProfileMenuOpen(false);
                          navigate('/panel-empresa/restaurantes');
                        }}
                      >
                        {userChefIcon}
                        <span className="navigation__profile-menu-item">Mi Perfil</span>
                      </div>
                      <div
                        className="navigation__menu-item-container"
                        onClick={() => {
                          setProfileMenuOpen(false);
                          handleLogout();
                        }}
                      >
                        {signOutIcon}
                        <span className="navigation__profile-menu-item">Cerrar sesión</span>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="navigation__menu-item-container">
                        {userChefIcon}
                        <Link to="/panel-empleado/perfil-empleado" className="navigation__profile-menu-item">Mi Perfil</Link>
                      </div>
                      <div className="navigation__menu-item-container">
                        {signOutIcon}
                        <Link onClick={handleLogout} className="navigation__profile-menu-item">Logout</Link>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className='navigation__menu-icon-container'>
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          aria-expanded={menuOpen}
          aria-controls="menu-list"
          className="navigation__menu-icon"
        >
          <FontAwesomeIcon icon={faBars} />
        </button>
      </div>
    </nav>
  );
};

export default Navigation;

